import React, { useEffect, useState } from 'react';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { Modal, Box, Typography, Button } from '@mui/material';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';

import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import SaveIcon from '@mui/icons-material/Save';

import { useDispatch, useSelector } from 'react-redux';

import { selectRegressionModalOpen, updateRegressionModalOpen } from '../../redux/app/configSlice';

import {
  getAllRegressionTestsAsync,
  createRegressionTestAsync,
  updateRegressionTestAsync,
  deleteRegressionTestAsync,
  selectRegressionList,
  selectRegressionLoading,
} from '../../redux/regressionTestIds/regressionCrudSlice';

import { getRegressionTestIdsAsync } from '../../redux/regressionTestIds/getRegressionTestIdsSlice';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';

export default function RegressionIdsModal() {
  const dispatch = useDispatch();

  const open = useSelector(selectRegressionModalOpen);
  const list = useSelector(selectRegressionList);
  const loading = useSelector(selectRegressionLoading);

  const [rows, setRows] = useState([]);
  const [rowModesModel, setRowModesModel] = useState({});

  const [errorMsg, setErrorMsg] = useState('');
  const [openSnackbar, setOpenSnackbar] = useState(false);

  const [successMsg, setSuccessMsg] = useState('');
  const [openSuccess, setOpenSuccess] = useState(false);

  const [isProcessing, setIsProcessing] = useState(false);

  /* ================= LOAD DATA ================= */
  useEffect(() => {
    if (open) {
      dispatch(getAllRegressionTestsAsync());
    }
  }, [open, dispatch]);

  useEffect(() => {
    const formatted = list.map((item) => ({
      id: item.id,
      regression_test_id: item.regression_test_id,
      description: item.description,
      configuration: item.configuration,
      build: item.build,
      version: item.version,
      is_active: item.is_active === 1,
      isNew: false,
    }));
    setRows(formatted);
  }, [list]);

  /* ================= ACTIONS ================= */

  const handleEditClick = (id) => () => {
    setRowModesModel((prev) => ({
      ...prev,
      [id]: { mode: 'edit' },
    }));
  };

  const handleSaveClick = (id) => () => {
    setRowModesModel((prev) => ({
      ...prev,
      [id]: { mode: 'view' },
    }));
  };

  const handleCancelClick = (id) => () => {
    setRowModesModel((prev) => ({
      ...prev,
      [id]: { mode: 'view', ignoreModifications: true },
    }));

    const row = rows.find((r) => r.id === id);
    if (row?.isNew) {
      setRows((prev) => prev.filter((r) => r.id !== id));
    }
  };

  const handleDeleteClick = (id) => async () => {
    if (window.confirm('Delete this Regression ID?')) {
      setIsProcessing(true);

      const res = await dispatch(deleteRegressionTestAsync(id));

      if (!res.success) {
        setErrorMsg(res.message);
        setOpenSnackbar(true);
        setIsProcessing(false);
        return;
      }

      // SUCCESS
      setSuccessMsg('Deleted Regression ID successfully');
      setOpenSuccess(true);

      //  render immedialy on UI
      setRows((prev) => prev.filter((r) => r.id !== id));

      dispatch(getRegressionTestIdsAsync());

      setIsProcessing(false);
    }
  };

  const processRowUpdate = async (newRow) => {
    setIsProcessing(true);

    try {
      if (!newRow.regression_test_id?.trim()) {
        throw new Error('Regression ID is required');
      }

      let res;

      if (newRow.isNew) {
        res = await dispatch(
          createRegressionTestAsync({
            regression_test_id: newRow.regression_test_id,
            description: newRow.description,
            configuration: newRow.configuration,
            build: newRow.build,
            version: newRow.version,
          }),
        );
      } else {
        res = await dispatch(
          updateRegressionTestAsync(newRow.id, {
            regression_test_id: newRow.regression_test_id,
            description: newRow.description,
            configuration: newRow.configuration,
            build: newRow.build,
            version: newRow.version,
            is_active: newRow.is_active ? 1 : 0,
          }),
        );
      }

      if (!res.success) {
        throw new Error(res.message);
      }

      // SUCCESS MESSAGE
      setSuccessMsg(
        newRow.isNew
          ? 'Created New Regression ID successfully'
          : 'Updated Regression ID successfully',
      );
      setOpenSuccess(true);

      //  render immedialy on UI
      setRows((prev) =>
        prev.map((row) => (row.id === newRow.id ? { ...newRow, isNew: false } : row)),
      );

      dispatch(getRegressionTestIdsAsync());

      return { ...newRow, isNew: false };
    } catch (err) {
      setErrorMsg(err.message);
      setOpenSnackbar(true);
      throw err;
    } finally {
      setIsProcessing(false);
    }
  };

  /* ================= COLUMNS ================= */

  const columns = [
    {
      field: 'regression_test_id',
      headerName: 'Regression ID',
      flex: 2,
      editable: true,
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 2,
      editable: true,
    },
    {
      field: 'configuration',
      headerName: 'Configuration',
      flex: 2,
      editable: true,
    },
    {
      field: 'build',
      headerName: 'Build',
      flex: 2,
      editable: true,
    },
    {
      field: 'version',
      headerName: 'Version',
      flex: 2,
      editable: true,
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      getActions: ({ id }) => {
        const isEditing = rowModesModel[id]?.mode === 'edit';

        if (isEditing) {
          return [
            <GridActionsCellItem
              key="save"
              icon={<SaveIcon />}
              label="Save"
              onClick={handleSaveClick(id)}
              disabled={isProcessing}
            />,
            <GridActionsCellItem
              key="cancel"
              icon={<CloseIcon />}
              label="Cancel"
              onClick={handleCancelClick(id)}
              disabled={isProcessing}
            />,
          ];
        }

        return [
          <GridActionsCellItem
            key="edit"
            icon={<EditIcon />}
            label="Edit"
            onClick={handleEditClick(id)}
            disabled={isProcessing}
          />,
          <GridActionsCellItem
            key="delete"
            icon={<DeleteIcon />}
            label="Delete"
            onClick={handleDeleteClick(id)}
            disabled={isProcessing}
          />,
        ];
      },
    },
  ];

  return (
    <>
      <Modal open={open} onClose={() => dispatch(updateRegressionModalOpen(false))}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '80%',
            height: '80%',
            bgcolor: 'background.paper',
            borderRadius: 3,
            p: 2,

            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Typography variant="h6"></Typography>

            <IconButton
              onClick={() => dispatch(updateRegressionModalOpen(false))}
              sx={{
                color: 'text.secondary',
                '&:hover': {
                  color: 'text.primary',
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{ mb: 2 }}>
            <Button
              startIcon={<AddIcon />}
              onClick={() => {
                const id = `new_${Date.now()}`;
                setRows((prev) => [
                  {
                    id,
                    regression_test_id: '',
                    description: '',
                    configuration: '',
                    build: '',
                    version: '',
                    is_active: true,
                    isNew: true,
                  },
                  ...prev,
                ]);

                setRowModesModel((prev) => ({
                  ...prev,
                  [id]: { mode: 'edit' },
                }));
              }}
              sx={{ mb: 2 }}
            >
              Add Regression ID
            </Button>
          </Box>
          <Box sx={{ flexGrow: 1, minHeight: 0 }}>
            <DataGrid
              rows={rows}
              columns={columns}
              loading={loading || isProcessing}
              editMode="row"
              rowModesModel={rowModesModel}
              onRowModesModelChange={setRowModesModel}
              processRowUpdate={processRowUpdate}
              onProcessRowUpdateError={() => {}}
            />
          </Box>
        </Box>
      </Modal>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" variant="filled" onClose={() => setOpenSnackbar(false)}>
          {errorMsg}
        </Alert>
      </Snackbar>
      <Snackbar
        open={openSuccess}
        autoHideDuration={3000}
        onClose={() => setOpenSuccess(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" variant="filled" onClose={() => setOpenSuccess(false)}>
          {successMsg}
        </Alert>
      </Snackbar>
    </>
  );
}
